import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { 
  Calendar, 
  ChevronLeft, 
  Search, 
  RotateCcw, 
  User, 
  LogOut, 
  Edit2, 
  History, 
  ArrowRight, 
  Package, 
  RefreshCw, 
  Trash2, 
  Plus,
  Users,
  Clock,
} from 'lucide-react';
import Footer from './Footer';
import MyInfoModal from './MyInfoModal';
import EditOrderBottomSheet from './EditOrderBottomSheet';
import {
  Order,
  UserProfile,
  OrderHistory as IOrderHistory,
  OrderItem,
  OrderHistoryGroupItem,
  OrderSheetGroup,
  UnavailableOrderItem,
  WaitlistEntry,
} from '../types';
import { getOrderHistory } from '../api/history';
import { createOrder, getOrder, updateOrder } from '../api/orders';
import { getCustomerOrderHistoryPage } from '../api/customer-pages';
import { cancelWaitlist } from '../api/waitlists';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import {
  getOrderItemDisplayName,
  getOrderItemOptionLabel,
} from '../utils/orderItemDisplay';


interface OrderHistoryProps {
  onBack: () => void;
  profile: UserProfile & { id?: string };
  onUpdateProfile: (profile: UserProfile) => void;
  onLogout: () => void;
}

const formatUnavailableItemsMessage = (items: UnavailableOrderItem[]) =>
  items
    .map((item) => {
      const availabilityLabel =
        item.availableQuantity > 0
          ? `가능 ${item.availableQuantity}개`
          : '품절';
      return `- ${item.name} (${availabilityLabel})`;
    })
    .join('\n');

const sanitizeImageUrl = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

const getOrderItemAvailability = (item: OrderItem) => {
  const isUnavailable = Boolean(item.isProductDeleted);

  return {
    isUnavailable,
    label: item.availabilityLabel || '판매종료',
  };
};

const getVisibleOrderItems = (items: OrderItem[]) =>
  items.filter((item) => item.quantity > 0);

const buildOrderItemKey = (
  groupKey: string,
  item: OrderItem,
  index: number,
) => `${groupKey}-${item.id || `${item.productId}-${item.productOptionId || 'base'}-${index}`}`;

const isOrderEditable = (order: Order) =>
  !['COMPLETED', 'CANCELLED'].includes(order.status)
  && order.orderSheet?.isClosed !== true;

const getOrderHistoryFamilyId = (order: Order) =>
  `${order.storeId}::${order.orderSheetId || order.id}`;

const getOrderSheetGroupKey = (order: Order) =>
  getOrderHistoryFamilyId(order);

const getGroupedOrderStatus = (orders: Order[]): Order['status'] => {
  if (orders.some((order) => order.status === 'PENDING')) {
    return 'PENDING';
  }
  if (orders.some((order) => order.status === 'READY')) {
    return 'READY';
  }
  if (orders.some((order) => order.status === 'COMPLETED')) {
    return 'COMPLETED';
  }
  return 'CANCELLED';
};

const ORDER_ITEM_STATUS_PRIORITY: Record<Order['status'], number> = {
  PENDING: 0,
  READY: 1,
  COMPLETED: 2,
  CANCELLED: 3,
};

const sortDisplayItems = (items: OrderHistoryGroupItem[]) =>
  [...items].sort((a, b) => {
    const priorityDiff =
      ORDER_ITEM_STATUS_PRIORITY[a.status] - ORDER_ITEM_STATUS_PRIORITY[b.status];
    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    const nameDiff = a.name.localeCompare(b.name, 'ko-KR');
    if (nameDiff !== 0) {
      return nameDiff;
    }

    const optionDiff = (a.optionName || '').localeCompare(
      b.optionName || '',
      'ko-KR',
    );
    if (optionDiff !== 0) {
      return optionDiff;
    }

    return a.price - b.price;
  });

const buildOrderSheetGroup = (
  key: string,
  groupedOrders: Order[],
): OrderSheetGroup | null => {
  if (groupedOrders.length === 0) {
    return null;
  }

  const sourceOrders = [...groupedOrders].sort(
    (a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  const latestOrder = sourceOrders[sourceOrders.length - 1];
  const editableSourceOrders = sourceOrders.filter(isOrderEditable);
  const latestEditableOrder = [...editableSourceOrders].reverse()[0];
  const representativeOrder =
    latestEditableOrder
    || [...sourceOrders].reverse().find((order) => order.status !== 'CANCELLED')
    || latestOrder;
  const requestSource =
    [...sourceOrders].reverse().find((order) => order.request?.trim())
    || representativeOrder;
  const displayItemMap = new Map<string, OrderHistoryGroupItem>();
  const editableItemMap = new Map<string, OrderItem>();

  sourceOrders.forEach((order) => {
    getVisibleOrderItems(order.items).forEach((item) => {
      const itemKey = `${item.productId || `CUSTOM:${item.name}`}::${item.productOptionId || ''}::${item.price}::${order.status}`;
      const existingItem = displayItemMap.get(itemKey);

      if (existingItem) {
        existingItem.quantity += item.quantity;
        return;
      }

      displayItemMap.set(itemKey, { ...item, status: order.status });
    });
  });

  editableSourceOrders.forEach((order) => {
    getVisibleOrderItems(order.items).forEach((item) => {
      const itemKey = `${item.productId || `CUSTOM:${item.name}`}::${item.productOptionId || ''}::${item.price}`;
      const existingItem = editableItemMap.get(itemKey);

      if (existingItem) {
        existingItem.quantity += item.quantity;
        return;
      }

      editableItemMap.set(itemKey, { ...item });
    });
  });

  const displayItems = sortDisplayItems(Array.from(displayItemMap.values()));
  const editableItems = Array.from(editableItemMap.values());
  const activeItemCount = displayItems
    .filter((item) => item.status === 'PENDING' || item.status === 'READY')
    .reduce((sum, item) => sum + item.quantity, 0);
  const completedItemCount = displayItems
    .filter((item) => item.status === 'COMPLETED')
    .reduce((sum, item) => sum + item.quantity, 0);
  const cancelledItemCount = displayItems
    .filter((item) => item.status === 'CANCELLED')
    .reduce((sum, item) => sum + item.quantity, 0);
  const visibleTotalPrice = displayItems
    .filter((item) => item.status !== 'CANCELLED')
    .reduce((sum, item) => sum + item.price * item.quantity, 0);
  const statusSourceOrders =
    editableSourceOrders.length > 0 ? editableSourceOrders : sourceOrders;

  return {
    key,
    sourceOrders,
    editableSourceOrders,
    orderSheetLabel:
      latestOrder.orderSheet?.label
      || sourceOrders[0].orderSheet?.label
      || '미지정 주문지',
    displayDate: latestOrder.date,
    displayItems,
    visibleTotalPrice,
    activeItemCount,
    completedItemCount,
    cancelledItemCount,
    order: {
      ...representativeOrder,
      items: editableItems,
      request: requestSource.request,
      totalPrice: visibleTotalPrice,
      status: getGroupedOrderStatus(statusSourceOrders),
    },
  };
};

const buildOrderSheetGroups = (orderList: Order[]): OrderSheetGroup[] => {
  const grouped = new Map<string, Order[]>();

  orderList.forEach((order) => {
    const key = getOrderSheetGroupKey(order);
    const current = grouped.get(key) || [];
    current.push(order);
    grouped.set(key, current);
  });

  return Array.from(grouped.entries())
    .map(([key, groupedOrders]) => buildOrderSheetGroup(key, groupedOrders))
    .filter((group): group is OrderSheetGroup => Boolean(group))
    .sort(
      (a, b) =>
        new Date(
          b.sourceOrders[b.sourceOrders.length - 1]?.createdAt || b.order.createdAt,
        ).getTime()
        - new Date(
          a.sourceOrders[a.sourceOrders.length - 1]?.createdAt || a.order.createdAt,
        ).getTime(),
    );
};

const replaceOrdersById = (currentOrders: Order[], updatedOrders: Order[]) => {
  const updatedMap = new Map(updatedOrders.map((order) => [order.id, order]));
  const mergedOrders = currentOrders.map(
    (order) => updatedMap.get(order.id) || order,
  );

  updatedOrders.forEach((order) => {
    if (!mergedOrders.some((existingOrder) => existingOrder.id === order.id)) {
      mergedOrders.push(order);
    }
  });

  return mergedOrders;
};

const STATUS_CONFIG: Record<string, { label: string; className: string; dot: string }> = {
  PENDING: {
    label: '주문완료',
    className: 'bg-blue-50 text-blue-600 border border-blue-100',
    dot: 'bg-blue-400',
  },
  READY: {
    label: '상품준비완료',
    className: 'bg-amber-50 text-amber-600 border border-amber-200',
    dot: 'bg-amber-400',
  },
  COMPLETED: {
    label: '입금/수령완료',
    className: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    dot: 'bg-emerald-500',
  },
  CANCELLED: {
    label: '취소됨',
    className: 'bg-red-50 text-red-500 border border-red-100',
    dot: 'bg-red-400',
  },
};

const shouldShowStatusBadge = (status?: string | null) =>
  Boolean(status && status !== 'CANCELLED');

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  if (!shouldShowStatusBadge(status)) {
    return null;
  }

  const config = STATUS_CONFIG[status] || { label: status, className: 'bg-gray-100 text-gray-500 border border-gray-200', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-black px-2 py-1 rounded-full ${config.className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
};

const TimelineStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config = STATUS_CONFIG[status] || { label: status, className: 'bg-gray-100 text-gray-500 border border-gray-200', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-black px-2 py-1 rounded-full ${config.className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
};

const ACTION_LABEL: Record<string, string> = {
  UPDATE_ITEMS: '품목 수정',
  UPDATE_PRICE: '상품 가격 변경',
  UPDATE_STATUS: '상태 변경',
  UPDATE_INFO: '정보 변경',
  CREATE: '주문 생성',
  CANCEL: '주문 취소',
  BATCH_UPDATE: '일괄 상태 변경',
  PARTIAL_SPLIT_COMPLETE: '일부 품목 상태 변경',
  STOCK_RESTORED_BY_ORDER_CANCEL: '재고 복구 (취소)',
  STOCK_DEDUCTED_BY_ORDER: '재고 차감',
};

const OrderHistory: React.FC<OrderHistoryProps> = ({ 
  onBack, 
  profile, 
  onUpdateProfile, 
  onLogout, 
}) => {
  const [isMyInfoOpen, setIsMyInfoOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [waitlists, setWaitlists] = useState<WaitlistEntry[]>([]);
  const [cancellingWaitlistId, setCancellingWaitlistId] = useState<string | null>(null);
  const [expandedHistory, setExpandedHistory] = useState<Record<string, IOrderHistory[]>>({});
  const [loadingHistory, setLoadingHistory] = useState<Record<string, boolean>>({});
  const [storeEditAvailability, setStoreEditAvailability] = useState<
    Record<string, boolean>
  >({});
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [editingOrderGroup, setEditingOrderGroup] = useState<OrderSheetGroup | null>(null);

  const loadHistoryPage = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setIsLoadingPage(true);
    }
    try {
      const pageData = await getCustomerOrderHistoryPage({
        name: profile.name,
        phone: profile.phone,
      });
      setOrders(pageData.orders);
      setWaitlists(pageData.waitlists);
      setStoreEditAvailability(pageData.storeEditAvailability || {});
    } catch (error) {
      console.error('Failed to fetch customer order history page', error);
      setOrders([]);
      setWaitlists([]);
      setStoreEditAvailability({});
    } finally {
      if (showLoading) {
        setIsLoadingPage(false);
      }
    }
  }, [profile.name, profile.phone]);

  useEffect(() => {
    void loadHistoryPage();
  }, [loadHistoryPage]);

  const handleCancelWaitlist = useCallback(async (entry: WaitlistEntry) => {
    const productLabel = entry.optionName
      ? `${entry.productName || '상품'} (${entry.optionName})`
      : entry.productName || '상품';

    if (
      !window.confirm(
        `${productLabel}의 남은 ${entry.remainingQuantity}개 줄서기를 취소하시겠습니까? 이미 주문에 반영된 수량은 유지됩니다.`,
      )
    ) {
      return;
    }

    setCancellingWaitlistId(entry.id);
    try {
      await cancelWaitlist(entry.id);
      setWaitlists((current) => current.filter((item) => item.id !== entry.id));
      await loadHistoryPage(false);
      alert('줄서기가 취소되었습니다.');
    } catch (error: any) {
      console.error('Failed to cancel waitlist', error);
      await loadHistoryPage(false);
      alert(
        error?.response?.data?.message
        || '줄서기를 취소하지 못했습니다. 잠시 후 다시 시도해주세요.',
      );
    } finally {
      setCancellingWaitlistId(null);
    }
  }, [loadHistoryPage]);

  const reloadEditingOrderGroup = useCallback(async () => {
    if (!editingOrderGroup) {
      return null;
    }

    if (editingOrderGroup.editableSourceOrders.length === 0) {
      return null;
    }

    const latestOrders = await Promise.all(
      editingOrderGroup.editableSourceOrders.map((order) => getOrder(order.id)),
    );

    setOrders((prev) => replaceOrdersById(prev, latestOrders));

    const nextSourceOrders = replaceOrdersById(
      editingOrderGroup.sourceOrders,
      latestOrders,
    );
    const nextGroup = buildOrderSheetGroup(
      editingOrderGroup.key,
      nextSourceOrders,
    );
    if (!nextGroup) {
      setEditingOrderGroup(null);
      return null;
    }

    const nextEditingGroup =
      editingOrderGroup.acceptsOrderEdit === undefined
        ? nextGroup
        : {
            ...nextGroup,
            acceptsOrderEdit: editingOrderGroup.acceptsOrderEdit,
          };

    setEditingOrderGroup(nextEditingGroup);
    setExpandedHistory((prev) => {
      const next = { ...prev };
      delete next[editingOrderGroup.key];
      return next;
    });
    setLoadingHistory((prev) => {
      const next = { ...prev };
      delete next[editingOrderGroup.key];
      return next;
    });

    return nextEditingGroup.order;
  }, [editingOrderGroup]);

  /** Human-readable description of a single item change */
  const describeItemChange = (change: { productId?: string; name: string; optionName?: string | null; oldQty: number; newQty: number; diff: number; changeKind?: string }) => {
    const label = change.name;
    if (change.changeKind === 'STATUS_ONLY' || change.diff === 0) {
      return {
        text: `${label} ${change.newQty}개 상태 변경`,
        type: 'status' as const,
      };
    }
    if (change.newQty === 0) return { text: `${label} 취소`, type: 'remove' as const };
    if (change.oldQty === 0) return { text: `${label} ${change.newQty}개 추가`, type: 'add' as const };
    return {
      text: `${label} ${change.oldQty}개 → ${change.newQty}개`,
      type: change.diff > 0 ? ('increase' as const) : ('decrease' as const),
    };
  };

  const ItemChangeLine: React.FC<{ change: any }> = ({ change }) => {
    const { text, type } = describeItemChange(change);
    const iconProps = { size: 10, strokeWidth: 3 };
    return (
      <div className="flex items-center gap-2 py-0.5">
        <span
          className={`shrink-0 w-4 h-4 flex items-center justify-center rounded-full ${
            type === 'remove'
              ? 'bg-red-100 text-red-500'
              : type === 'add'
              ? 'bg-green-100 text-green-600'
              : type === 'status'
              ? 'bg-slate-100 text-slate-500'
              : type === 'increase'
              ? 'bg-blue-100 text-blue-500'
              : 'bg-orange-100 text-orange-500'
          }`}
        >
          {type === 'remove' ? <Trash2 {...iconProps} /> : type === 'add' ? <Plus {...iconProps} /> : <RefreshCw {...iconProps} />}
        </span>
        <span
          className={`text-[11px] font-bold ${
            type === 'remove'
              ? 'text-red-500 line-through'
              : type === 'add'
              ? 'text-green-600'
              : type === 'status'
              ? 'text-slate-500'
              : type === 'increase'
              ? 'text-blue-600'
              : 'text-orange-500'
          }`}
        >
          {text}
        </span>
      </div>
    );
  };

  const OrderEditTimeline: React.FC<{ logs: IOrderHistory[] }> = ({ logs }) => {
    if (logs.length === 0) {
      return (
        <div className="mt-3 p-4 bg-gray-50 rounded-2xl text-center animate-in fade-in duration-200">
          <p className="text-[11px] text-gray-400">변경 이력이 없습니다.</p>
        </div>
      );
    }

    return (
      <div className="mt-3 animate-in fade-in slide-in-from-top-2 duration-200">
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[11px] top-3 bottom-3 w-px bg-gray-200" />

          <div className="space-y-4">
            {logs.map((log, logIdx) => {
              const isStatusChange = log.oldStatus !== log.newStatus && !!log.oldStatus;
              const hasItemChanges = (log.details?.itemChanges?.length || 0) > 0;
              const priceChanges = Array.isArray(log.details?.priceChanges)
                ? log.details.priceChanges
                : [];
              const hasPriceChange =
                log.details?.newPrice !== undefined && log.details?.oldPrice !== undefined;
              const isByBuyer = log.modifierRole === 'CUSTOMER' || log.modifierRole === 'USER';
              const isFirst = logIdx === logs.length - 1; // oldest = first chronologically

              return (
                <div key={log.id} className="flex gap-3 relative">
                  {/* Timeline dot */}
                  <div
                    className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center z-10 mt-0.5 ${
                      isFirst
                        ? 'bg-emerald-500 text-white'
                        : isStatusChange
                        ? 'bg-amber-100 border-2 border-amber-400'
                        : 'bg-white border-2 border-gray-300'
                    }`}
                  >
                    {isFirst ? (
                      <Package size={11} strokeWidth={2.5} />
                    ) : isStatusChange ? (
                      <ArrowRight size={10} className="text-amber-500" strokeWidth={3} />
                    ) : (
                      <RefreshCw size={10} className="text-gray-400" strokeWidth={2.5} />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-1">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span
                          className={`text-[10px] font-black px-1.5 py-0.5 rounded-md ${
                            isByBuyer
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {isByBuyer ? '본인' : '판매자'}
                        </span>
                        <span className="text-[11px] font-black text-gray-800">
                          {ACTION_LABEL[log.action || ''] || log.action || '변경'}
                        </span>
                      </div>
                      <span className="text-[10px] font-bold text-gray-400 shrink-0">
                        {format(new Date(log.createdAt), 'M.d HH:mm', { locale: ko })}
                      </span>
                    </div>

                    {/* Status transition */}
                    {isStatusChange && log.oldStatus && log.newStatus && (
                      <div className="flex items-center gap-1 mb-1.5 flex-wrap">
                        <TimelineStatusBadge status={log.oldStatus} />
                        <ArrowRight size={12} className="text-gray-300" />
                        <TimelineStatusBadge status={log.newStatus} />
                      </div>
                    )}

                    {/* Item changes */}
                    {hasItemChanges && (
                      <div className="bg-white rounded-xl border border-gray-100 px-3 py-2 space-y-0.5 shadow-sm">
                        {log.details.itemChanges.map((change: any, idx: number) => (
                          <ItemChangeLine key={idx} change={change} />
                        ))}
                      </div>
                    )}

                    {priceChanges.length > 0 && (
                      <div className="bg-white rounded-xl border border-gray-100 px-3 py-2 space-y-1 shadow-sm">
                        {priceChanges.map((change: any, idx: number) => (
                          <div key={idx} className="text-[10px] font-bold text-gray-500">
                            <span className="text-gray-800">{change.name}</span>{' '}
                            {change.oldUnitPrice.toLocaleString()}원 →{' '}
                            <span className="text-emerald-700">
                              {change.newUnitPrice.toLocaleString()}원
                            </span>{' '}
                            · {change.quantity}개
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Price change */}
                    {hasPriceChange && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-bold text-gray-500 bg-white rounded-lg px-2.5 py-1.5 border border-gray-100 w-fit">
                        <span>{log.details.oldPrice.toLocaleString()}원</span>
                        <ArrowRight size={10} className="text-gray-300" />
                        <span className="text-gray-800">{log.details.newPrice.toLocaleString()}원</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const groupedOrders = useMemo(
    () =>
      buildOrderSheetGroups(orders).filter(
        (group) => group.order.status !== 'CANCELLED',
      ),
    [orders],
  );

  const filteredOrderSheetGroups = useMemo(() => {
    const loweredSearchTerm = searchTerm.toLowerCase();

    return groupedOrders.filter((group) =>
      group.order.storeName.toLowerCase().includes(loweredSearchTerm)
      || group.orderSheetLabel.toLowerCase().includes(loweredSearchTerm)
      || group.displayItems.some((item) =>
        item.name.toLowerCase().includes(loweredSearchTerm),
      )
      || group.displayItems.some((item) =>
        getOrderItemOptionLabel(item).toLowerCase().includes(loweredSearchTerm),
      ),
    );
  }, [groupedOrders, searchTerm]);

  const filteredWaitlists = useMemo(() => {
    const loweredSearchTerm = searchTerm.trim().toLowerCase();

    return waitlists.filter((entry) =>
      (entry.productName || '').toLowerCase().includes(loweredSearchTerm)
      || (entry.optionName || '').toLowerCase().includes(loweredSearchTerm)
      || (entry.storeName || '').toLowerCase().includes(loweredSearchTerm),
    );
  }, [searchTerm, waitlists]);

  const handleFetchHistory = async (group: OrderSheetGroup) => {
    if (expandedHistory[group.key]) {
      const newHistory = { ...expandedHistory };
      delete newHistory[group.key];
      setExpandedHistory(newHistory);
      return;
    }

    setLoadingHistory(prev => ({ ...prev, [group.key]: true }));
    try {
      const relatedOrders = Array.from(
        new Map<string, Order>(
          orders
            .filter(
              (order) =>
                getOrderHistoryFamilyId(order)
                === getOrderHistoryFamilyId(group.order),
            )
            .map((order) => [order.id, order]),
        ).values(),
      );
      const historyResponses = await Promise.all(
        relatedOrders.map((order) => getOrderHistory(order.id)),
      );
      const history = historyResponses
        .flat()
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime()
            - new Date(a.createdAt).getTime(),
        );
      setExpandedHistory(prev => ({ ...prev, [group.key]: history }));
    } catch (error) {
      console.error('Failed to fetch order history', error);
    } finally {
      setLoadingHistory(prev => ({ ...prev, [group.key]: false }));
    }
  };

  const handleEditOrderSave = async (items: OrderItem[]) => {
    if (!editingOrderGroup) {
      return;
    }

    try {
      const visibleItems = items.filter((item) => item.quantity > 0);
      const sourceOrderIds = editingOrderGroup.editableSourceOrders.map(
        (order) => order.id,
      );
      let successAlertMessage = '주문이 수정되었습니다.';

      if (visibleItems.length === 0) {
        await Promise.all(
          sourceOrderIds.map((orderId) =>
            updateOrder(orderId, { status: 'CANCELLED' }),
          ),
        );
        successAlertMessage = '주문이 취소되었습니다.';
      } else {
        const totalPrice = visibleItems.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0,
        );

        const { order, unavailableItems } = await createOrder({
          ...(editingOrderGroup.order.orderSheetId
            ? { orderSheetId: editingOrderGroup.order.orderSheetId }
            : {}),
          storeId: editingOrderGroup.order.storeId,
          storeName: editingOrderGroup.order.storeName,
          items: visibleItems.map((item) => ({
            productId: item.productId,
            productOptionId: item.productOptionId,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            image: item.image,
          })),
          totalPrice,
          customerName:
            profile.name.trim() || editingOrderGroup.order.customerName,
          customerPhone:
            profile.phone.trim() || editingOrderGroup.order.customerPhone || '',
          request: editingOrderGroup.order.request,
          userId: profile.id,
        });

        if (!order) {
          alert(
            `주문 가능한 상품이 없어 수정되지 않았습니다.\n\n제외 상품:\n${formatUnavailableItemsMessage(unavailableItems)}`,
          );
          return;
        }

        if (unavailableItems.length > 0) {
          successAlertMessage = `일부 상품은 품절 또는 주문 불가로 제외되고 나머지만 수정되었습니다.\n\n제외 상품:\n${formatUnavailableItemsMessage(unavailableItems)}`;
        }
      }

      await loadHistoryPage();
      setEditingOrderGroup(null);
      alert(successAlertMessage);
    } catch (error: any) {
      console.error('Failed to save order edit:', error);
      const message = error.response?.data?.message || '주문 수정에 실패했습니다.';
      alert(message);
    }
  };

  return (
    <div className="min-h-screen flex flex-col max-w-lg mx-auto bg-white shadow-xl relative pb-20">
      <div className="p-5 flex items-center bg-white sticky top-0 z-50 border-b shadow-sm">
        <button onClick={onBack} className="mr-3 p-2 hover:bg-gray-100 rounded-full transition-colors">
          <ChevronLeft size={24} className="text-gray-800" />
        </button>
        <h1 className="text-xl font-black text-gray-900">나의 주문내역</h1>
        <div className="ml-auto flex gap-2">
          <button onClick={() => setIsMyInfoOpen(true)} className="h-9 px-3 rounded-xl bg-gray-50 text-gray-600 text-[13px] font-bold flex items-center gap-1.5 border">
            <User size={14} /> My 정보
          </button>
          <button onClick={onLogout} className="h-9 px-3 rounded-xl bg-gray-50 text-red-500 text-[13px] font-bold flex items-center gap-1.5 border border-red-100 active:bg-red-50">
            <LogOut size={14} /> 로그아웃
          </button>
        </div>
      </div>

      <div className="px-6 pt-6 pb-6 bg-gray-50/50">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            className="w-full h-12 bg-white border border-gray-100 rounded-2xl pl-11 pr-4 text-sm font-bold outline-none focus:border-emerald-500 shadow-sm transition-all"
            placeholder="주문·줄서기 상품 또는 배송지 검색..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="px-6 py-4 flex-1">
        {isLoadingPage ? (
          <div className="py-20 text-center text-gray-400 font-bold text-sm">
            주문 내역을 불러오는 중입니다.
          </div>
        ) : (
          <div className="space-y-8">
            {filteredWaitlists.length > 0 && (
              <section>
                <div className="mb-3 flex items-end justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Users size={17} className="text-violet-600" />
                      <h2 className="text-base font-black text-gray-900">
                        줄서기 내역
                      </h2>
                      <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-black text-violet-700">
                        {filteredWaitlists.reduce(
                          (sum, entry) => sum + entry.remainingQuantity,
                          0,
                        )}개
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] font-bold text-gray-400">
                      재고가 생기면 순서대로 주문에 자동 반영됩니다.
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {filteredWaitlists.map((entry) => (
                    <div
                      key={entry.id}
                      className="rounded-3xl border border-violet-100 bg-violet-50/50 p-4 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[11px] font-black text-violet-500">
                            {entry.storeName || '공동구매 배송지'}
                          </p>
                          <h3 className="mt-1 break-words text-[15px] font-black leading-tight text-gray-900">
                            {entry.productName || '상품 정보 확인 중'}
                          </h3>
                          {entry.optionName && (
                            <p className="mt-1 text-[11px] font-black text-violet-600">
                              옵션: {entry.optionName}
                            </p>
                          )}
                          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-bold text-gray-400">
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {format(new Date(entry.createdAt), 'M월 d일 신청', {
                                locale: ko,
                              })}
                            </span>
                            <span>신청 {entry.quantity}개</span>
                            {entry.fulfilledQuantity > 0 && (
                              <span className="text-emerald-600">
                                주문 반영 {entry.fulfilledQuantity}개
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="shrink-0 rounded-2xl bg-white px-3 py-2 text-center shadow-sm ring-1 ring-violet-100">
                          <span className="block text-[10px] font-black text-gray-400">
                            남은 수량
                          </span>
                          <span className="mt-0.5 block text-sm font-black text-violet-700">
                            {entry.remainingQuantity}개
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => void handleCancelWaitlist(entry)}
                        disabled={cancellingWaitlistId !== null}
                        className="mt-4 flex h-10 w-full items-center justify-center gap-1.5 rounded-2xl border border-red-100 bg-white text-[12px] font-black text-red-500 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Trash2 size={14} />
                        {cancellingWaitlistId === entry.id
                          ? '취소 처리 중...'
                          : `대기 ${entry.remainingQuantity}개 취소`}
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section>
              {filteredWaitlists.length > 0 && (
                <h2 className="mb-3 text-base font-black text-gray-900">
                  주문 내역
                </h2>
              )}
              {filteredOrderSheetGroups.length > 0 ? (
                <div className="space-y-6">
            {filteredOrderSheetGroups.map((group) => {
              const {
                key,
                order,
                sourceOrders,
                orderSheetLabel,
                displayDate,
                visibleTotalPrice,
              } = group;
              const editable = group.editableSourceOrders.length > 0;
              const storeAllowsOrderEdit =
                storeEditAvailability[order.storeId] !== false;
              const shouldRenderEditButton = !isLoadingPage && storeAllowsOrderEdit;
              const hasMixedItemStatuses =
                group.completedItemCount > 0 || group.cancelledItemCount > 0;

              return (
                <div key={key} className="bg-white p-5 rounded-3xl border border-gray-100 shadow-sm">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-base font-black text-gray-900 truncate">{order.storeName}</h3>
                        {orderSheetLabel && (
                          <span className="shrink-0 text-[10px] font-bold bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded-md border border-emerald-100">
                            {orderSheetLabel}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1.5 text-gray-400">
                          <Calendar size={12} />
                          <span className="text-[11px] font-bold">주문: {displayDate}</span>
                        </div>
                        {order.orderSheet?.deliveryDate && (
                          <div className="flex items-center gap-1.5 text-emerald-600">
                            <RotateCcw size={12} className="rotate-180" />
                            <span className="text-[11px] font-black">
                              배송일 {new Date(order.orderSheet.deliveryDate).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' })}
                            </span>
                          </div>
                        )}
                        {sourceOrders.length > 1 && (
                          <div className="flex items-center gap-1.5 text-emerald-500">
                            <Package size={12} />
                            <span className="text-[11px] font-black">
                              같은 주문지 주문 {sourceOrders.length}건 묶음
                            </span>
                          </div>
                        )}
                        {hasMixedItemStatuses && (
                          <div className="flex items-center gap-1.5 text-gray-400">
                            <Package size={12} />
                            <span className="text-[11px] font-black">
                              진행 {group.activeItemCount}개 / 완료 {group.completedItemCount}개 / 취소 {group.cancelledItemCount}개
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0 ml-2">
                      <StatusBadge status={order.status} />
                      {shouldRenderEditButton && (
                        <button
                          onClick={() =>
                            editable
                              && setEditingOrderGroup({
                                ...group,
                                acceptsOrderEdit: storeAllowsOrderEdit,
                              })
                          }
                          disabled={!editable}
                          className={`flex items-center gap-1 text-[11px] font-black px-2 py-1 rounded-lg transition-colors ${!editable
                              ? 'bg-gray-50 text-gray-300 border border-gray-100 cursor-not-allowed'
                              : 'text-emerald-600 border border-emerald-100 bg-white hover:bg-emerald-50'
                            }`}
                        >
                          <Edit2 size={10} />
                          {editable ? '주문 수정' : '수정 불가'}
                        </button>
                      )}
                    </div>
                  </div>
                  {group.displayItems.map((item, idx) => {
                    const availability = getOrderItemAvailability(item);
                    const isCancelled = item.status === 'CANCELLED' || item.quantity === 0;
                    const displayName = getOrderItemDisplayName(item);
                    const optionLabel = getOrderItemOptionLabel(item);
                    const totalItemPrice = item.price * item.quantity;
                    const imageUrl = sanitizeImageUrl(item.image);
                    const isDimmed = isCancelled || availability.isUnavailable;

                    return (
                      <div
                        key={buildOrderItemKey(key, item, idx)}
                        className={`mb-3 last:mb-0 rounded-[24px] border p-3 shadow-sm ${
                          isDimmed
                            ? 'border-gray-200 bg-gray-50/80'
                            : 'border-emerald-100 bg-white'
                        }`}
                      >
                        <div className="flex gap-3 items-center">
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              className={`w-20 h-20 rounded-[20px] object-cover border bg-gray-50 ${
                                isDimmed ? 'grayscale opacity-70' : ''
                              }`}
                              alt={displayName}
                            />
                          ) : (
                            <div className="w-20 h-20 rounded-[20px] border bg-gray-50 flex items-center justify-center text-gray-300">
                              <Package size={24} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p
                                  className={`text-[15px] font-black leading-tight break-words ${
                                    isCancelled
                                      ? 'text-gray-400 line-through'
                                      : availability.isUnavailable
                                      ? 'text-gray-500'
                                      : 'text-gray-900'
                                  }`}
                                >
                                  {displayName}
                                </p>
                                {optionLabel && (
                                  <p
                                    className={`mt-1 text-[11px] font-black ${
                                      isDimmed ? 'text-gray-400' : 'text-emerald-600'
                                    }`}
                                  >
                                    옵션: {optionLabel}
                                  </p>
                                )}
                                <p
                                  className={`mt-2 text-[18px] font-black ${
                                    isDimmed ? 'text-gray-400' : 'text-emerald-600'
                                  }`}
                                >
                                  {totalItemPrice.toLocaleString()}원
                                </p>
                                <p className="mt-1 text-[11px] font-bold text-gray-400">
                                  수량 {item.quantity}개 · 개당 {item.price.toLocaleString()}원
                                </p>
                              </div>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                {hasMixedItemStatuses && (
                                  <StatusBadge status={item.status} />
                                )}
                                {availability.isUnavailable && (
                                  <span className="shrink-0 text-[9px] font-bold bg-gray-900 text-white px-1.5 py-0.5 rounded border border-gray-900">
                                    {availability.label}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="mt-4 pt-3 border-t border-gray-50 flex justify-between items-center">
                    <button
                      onClick={() => handleFetchHistory(group)}
                      className="flex items-center gap-1.5 text-[11px] font-black text-gray-400 hover:text-gray-900 transition-colors"
                    >
                      <History size={12} />
                      {expandedHistory[group.key] ? '이력 닫기' : '주문지 진행 이력'}
                    </button>
                    <div className="text-right">
                      <span className="text-[12px] font-bold text-gray-400 block -mb-1">주문지 총 금액</span>
                      <span className="text-base font-black text-gray-900">
                        {visibleTotalPrice.toLocaleString()}원
                      </span>
                    </div>
                  </div>

                  {expandedHistory[group.key] && (
                    <OrderEditTimeline logs={expandedHistory[group.key]} />
                  )}

                  {loadingHistory[group.key] && (
                    <div className="mt-4 p-4 text-center text-[11px] text-gray-400 italic">
                      이력 불러오는 중...
                    </div>
                  )}
                </div>
              );
            })}
                </div>
              ) : (
                <div className="py-16 text-center text-gray-400 font-bold text-sm">
                  {searchTerm.trim() && filteredWaitlists.length === 0
                    ? '검색 결과가 없습니다.'
                    : '주문 내역이 없습니다.'}
                </div>
              )}
            </section>
          </div>
        )}
      </div>

      {editingOrderGroup && (
        <EditOrderBottomSheet
          isOpen={!!editingOrderGroup}
          onClose={() => setEditingOrderGroup(null)}
          order={editingOrderGroup.order}
          orderLabel={`${editingOrderGroup.order.storeName} · ${editingOrderGroup.orderSheetLabel}`}
          onLoadLatestOrder={reloadEditingOrderGroup}
          onSave={handleEditOrderSave}
        />
      )}
      <MyInfoModal isOpen={isMyInfoOpen} onClose={() => setIsMyInfoOpen(false)} profile={profile} onProfileUpdate={onUpdateProfile} />
      <Footer />
    </div>
  );
};

export default OrderHistory;
